export enum BillEventType {
  BILL_CREATED = 'bill_created',
  BILL_CHANGED = 'bill_changed', 
  BILL_SENT = 'bill_sent',
  PAYMENT_RECEIVED = 'payment_received',
  PAYMENT_SENT = 'payment_sent',
  FULLY_PAID = 'fully_paid'
}

export interface BillEvent {
  id: string;
  type: BillEventType;
  timestamp: Date;
  triggeredBy?: string; // User ID who triggered the event
  triggeredByName?: string; // Display name of user who triggered the event
  details?: Record<string, unknown>; // Additional event-specific data
}

export interface BillCreatedEvent extends BillEvent {
  type: BillEventType.BILL_CREATED;
  details?: {
    generatedBy: string;
    generatedByName?: string;
  };
}

export interface BillChangedEvent extends BillEvent {
  type: BillEventType.BILL_CHANGED;
  details?: {
    changedFields?: string[];
    previousValues?: Record<string, unknown>;
  };
}

export interface BillSentEvent extends BillEvent {
  type: BillEventType.BILL_SENT;
  details?: {
    sentTo?: string[];
    deliveryMethod?: string;
  };
}

export interface PaymentReceivedEvent extends BillEvent {
  type: BillEventType.PAYMENT_RECEIVED;
  details: {
    amount: number;
    totalPaidAmount: number;
    remainingAmount: number;
    paymentMethod?: string;
    reference?: string;
  };
}

export interface PaymentSentEvent extends BillEvent {
  type: BillEventType.PAYMENT_SENT;
  details: {
    amount: number;
    recipient?: string;
    paymentMethod?: string;
    reference?: string;
  };
}

export interface FullyPaidEvent extends BillEvent {
  type: BillEventType.FULLY_PAID;
  details: {
    totalAmount: number;
    finalPaymentAmount?: number;
  };
}

// Union type for all possible bill events
export type BillEventUnion = 
  | BillCreatedEvent
  | BillChangedEvent
  | BillSentEvent
  | PaymentReceivedEvent
  | PaymentSentEvent
  | FullyPaidEvent;

// Helper function to create event IDs
export const generateEventId = (): string => {
  return `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to get event icon name (for Material-UI icons)
export const getEventIcon = (eventType: BillEventType): string => {
  switch (eventType) {
    case BillEventType.BILL_CREATED:
      return 'Receipt';
    case BillEventType.BILL_CHANGED:
      return 'Edit';
    case BillEventType.BILL_SENT:
      return 'Send';
    case BillEventType.PAYMENT_RECEIVED:
      return 'ArrowDownward';
    case BillEventType.PAYMENT_SENT:
      return 'ArrowUpward';
    case BillEventType.FULLY_PAID:
      return 'CheckCircle';
    default:
      return 'Event';
  }
};

// Helper function to get event color (for timeline styling)
export const getEventColor = (eventType: BillEventType): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (eventType) {
    case BillEventType.BILL_CREATED:
      return 'info';
    case BillEventType.BILL_CHANGED:
      return 'warning';
    case BillEventType.BILL_SENT:
      return 'primary';
    case BillEventType.PAYMENT_RECEIVED:
      return 'success';
    case BillEventType.PAYMENT_SENT:
      return 'secondary';
    case BillEventType.FULLY_PAID:
      return 'success';
    default:
      return 'primary';
  }
};